import { PanelProps } from '@blueprintjs/core';
import * as React from 'react';

import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { DataSourceCards } from 'src/containers/DataSourceCreation/DataSourceCards';
import {
  DataSourceCreationPanel,
  DataSourceCreationPanelProps,
} from 'src/containers/DataSourceCreation/DataSourceCreationPanel/DataSourceCreationPanel';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import {
  ProjectVirtualGraphCreationPanel,
  ProjectVirtualGraphCreationPanelProps,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/ProjectVirtualGraphCreationPanel';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { DataSource } from 'src/store/state/dataSources';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type DataSourceSelectionPanelProps = ProjectResourceDialogPanelProps & {
  type: DataSourceType;
};

export const DataSourceSelectionPanel: React.VFC<
  PanelProps<DataSourceSelectionPanelProps>
> = ({ closePanel, openPanel, setFooterProps, type }) => {
  const isMounted = useIsMounted();
  const dispatch = useReduxDispatch();

  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);

  const dataSources = React.useMemo(
    () => dataSourcesByType[type] || [],
    [dataSourcesByType, type]
  );
  const isPendingOptions = React.useMemo(() => {
    return dataSources.some((dataSource) => !dataSource.options);
  }, [dataSources]);

  const handleRefreshTables = React.useCallback(
    (dataSourceId: string) => {
      dispatch(refreshDataSourceTables(dataSourceId));
    },
    [dispatch]
  );

  const handleSelectionOfCreateNewDataSource = React.useCallback(() => {
    const props: DataSourceCreationPanelProps = { setFooterProps, type };
    openPanel({
      renderPanel: DataSourceCreationPanel,
      props,
    });
  }, [openPanel, setFooterProps, type]);

  const handleSelectionOfDataSource = React.useCallback(
    (selectedDataSource: DataSource) => {
      const props: ProjectVirtualGraphCreationPanelProps = {
        setFooterProps,
        dataSourceId: selectedDataSource.id,
      };
      handleRefreshTables(selectedDataSource.id);
      openPanel({
        renderPanel: ProjectVirtualGraphCreationPanel,
        props,
      });
    },
    [handleRefreshTables, openPanel, setFooterProps]
  );

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        handleBackButtonClick: closePanel,
      });
    }
  }, [closePanel, isMounted, setFooterProps]);

  return (
    <DataSourcePanelContainer connectionType={type}>
      <DataSourceCards
        isLoading={isPendingOptions}
        onCreateDataSource={handleSelectionOfCreateNewDataSource}
        onSelectDataSource={handleSelectionOfDataSource}
        type={type}
      />
    </DataSourcePanelContainer>
  );
};
